import React, { FC } from "react";
import { Recognition } from "@shared/models";
import { Button, Icon } from "@shared/components";
import { getGbFormattedShortDate } from "@shared/utils";
import { IconType } from "@shared/components/Icon/Icon";

import "./index.scss";

interface RecognitionListItemProps {
  recognition: Recognition;
  // Resolved by the caller, not derived here (overview-rebuild-pr4-plan.md §4) -- `RecognitionList`
  // already knows which tab it renders, so it calls `recognitionPersonName` and passes one already-
  // resolved string down, rather than this component branching on `showSentTo`/`showSentFrom` (former
  // props, removed here along with the `as User` cast that branch required).
  personName: string | null;
  onListItemActionBtnClick: (recognition: Recognition) => void;
}

/**
 * One "recent recognitions" row -- the designed white card (overview-rebuild-pr4-plan.md §3, Figma
 * node `SPARK_recognition_row`, 9083:18138). Its own Figma description records why it reflows rather
 * than absolutely positioning its date/action parts, the way the row it replaces did: this list moves
 * from a full-width page section into a narrower grid column in this same PR, so a row that cannot
 * move inside an instance would break the moment its column width changed. No part of this row is
 * absolutely positioned, for the same reason.
 *
 * The body merges the person's name and the note into ONE string (`{person} — {note}`) rather than a
 * separate "From/To" line. `personName` is optional data (`created_by` is nullable, `Recognition.tsx:16`)
 * -- when it is `null`, the note renders alone, with no leading em dash.
 */
const RecognitionListItem: FC<RecognitionListItemProps> = (props) => {
  const { recognition, personName, onListItemActionBtnClick } = props;

  return (
    <div className="recognition-list-item-wrapper">
      <div className="recognition-list-item-icon" onClick={() => onListItemActionBtnClick(recognition)}>
        <div className="core-value-icon" style={{ backgroundImage: `url("${recognition.core_value?.icon_url}")` }} />
        <Icon type={`core-value-score core-value-score-${recognition.score}` as IconType} />
      </div>
      <div className="recognition-list-item-info">
        <div className="item-title">{recognition.core_value?.title}</div>
        <div className="item-description">
          {personName ? `${personName} — ${recognition.description}` : recognition.description}
        </div>
      </div>
      {/* Short month, not `getGbFormattedDate`'s long one: the frame gives this slot 92px at 12px
          ("8 Sep 2026"), and measured in the browser "17 September 2026" wrapped to two lines and made
          the row 34px taller than its neighbours. `getGbFormattedShortDate` was added in PR3 for the
          badge cards' award dates, which hit the same 92px-ish constraint. */}
      <div className="recognition-created-info">{getGbFormattedShortDate(recognition.created_at)}</div>

      <Button className="action-button" onClick={() => onListItemActionBtnClick(recognition)}>
        <Icon type="arrow-forward" />
      </Button>
    </div>
  );
};

export default RecognitionListItem;
